import {
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Box } from '@mui/system';
import axios from 'axios';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import MultipleAndSingleImgValidateWithPreview from 'src/common/FileValidation/MultipleAndSingleImgValidateWithPreview';
import { TOKEN_KEY, url } from 'src/Helpers/utils/Constant';
import Toaster from 'src/Toaster/Toaster';
import { FormValues, IWebsitePopup } from './types';
import dayjs from 'dayjs';

type Props = {
  popup: IWebsitePopup;
  handleClose: () => void;
  setRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
};

// Convert ISO date to YYYY-MM-DD for <input type="date"> using dayjs local time conversion
function toDateInput(isoStr: string) {
  if (!isoStr) return '';
  return dayjs(isoStr).format('YYYY-MM-DD');
}

function UpdateWebsitePopup({ popup, handleClose, setRefreshTrigger }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: {
      website_link: popup.website_link,
      alt_text: popup.alt_text,
      status: popup.status,
      start_date: toDateInput(popup.start_date),
      end_date: toDateInput(popup.end_date),
    },
  });

  const [loading, setLoading] = useState(false);
  const Toast = Toaster();
  const token = localStorage.getItem(TOKEN_KEY);

  const onSubmit = async (values: FormValues) => {
    try {
      if (!token) {
        Toast.fire({
          icon: 'error',
          title: 'Authentication token missing. Please log in again.',
        });
        return;
      }
      setLoading(true);

      const formData = new FormData();
      formData.append('website_link', values.website_link);
      formData.append('alt_text', values.alt_text);
      formData.append('status', String(values.status));
      formData.append(
        'start_date',
        dayjs(values.start_date).format('YYYY-MM-DD'),
      );
      formData.append('end_date', dayjs(values.end_date).format('YYYY-MM-DD'));
      if (values.image && values.image[0]) {
        formData.append('image', values.image[0]);
      }

      const { data } = await axios.post(
        `${url}/api/v1/admin/website-popup`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (data.success) {
        Toast.fire({
          icon: 'success',
          title: data.message || 'Website popup updated successfully!',
        });
        setRefreshTrigger((prev) => prev + 1);
        handleClose();
      } else {
        Toast.fire({
          icon: 'error',
          title: data.message || 'Failed to update website popup.',
        });
      }
    } catch (err: any) {
      Toast.fire({
        icon: 'error',
        title: err?.response?.data?.message || 'Something went wrong',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} sx={{ p: 1 }}>
      <Grid container spacing={1}>
        {/* Website Link */}
        <Grid item xs={12}>
          <TextField
            margin='normal'
            fullWidth
            id='website_link_update'
            label='Website Link'
            type='url'
            variant='outlined'
            error={!!errors.website_link}
            helperText={errors.website_link?.message}
            {...register('website_link')}
          />
        </Grid>

        {/* Alt Text */}
        <Grid item xs={12}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='alt_text_update'
            label='Image Alt Text'
            type='text'
            variant='outlined'
            error={!!errors.alt_text}
            helperText={errors.alt_text?.message}
            {...register('alt_text', { required: 'Alt text is required' })}
          />
        </Grid>

        {/* Start Date */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='start_date_update'
            label='Start Date'
            type='date'
            variant='outlined'
            InputLabelProps={{ shrink: true }}
            error={!!errors.start_date}
            helperText={errors.start_date?.message}
            {...register('start_date', { required: 'Start date is required' })}
          />
        </Grid>

        {/* End Date */}
        <Grid item xs={12} sm={6}>
          <TextField
            margin='normal'
            fullWidth
            required
            id='end_date_update'
            label='End Date'
            type='date'
            variant='outlined'
            InputLabelProps={{ shrink: true }}
            error={!!errors.end_date}
            helperText={errors.end_date?.message}
            {...register('end_date', { required: 'End date is required' })}
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12}>
          <FormControl fullWidth variant='outlined' margin='normal'>
            <InputLabel id='status-label-update'>Status</InputLabel>
            <Select
              labelId='status-label-update'
              id='status_update'
              label='Status'
              defaultValue={popup.status}
              {...register('status')}
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Image Upload (optional for update) */}
        <Grid item xs={12}>
          <MultipleAndSingleImgValidateWithPreview
            register={register}
            clearErrors={clearErrors}
            setError={setError}
            errors={errors}
            label='Change Popup Image (optional)'
            fieldName='image'
            hideCustomLabel
            onlyImage
            mode='single'
          />
        </Grid>

        {/* Submit */}
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Button
            type='submit'
            fullWidth
            variant='contained'
            disableElevation
            disabled={loading}
            sx={{ py: 1.5 }}
          >
            {loading ? 'Updating...' : 'Update Website Popup'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

export default UpdateWebsitePopup;
